import React, { useCallback, useEffect, useState, useRef } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import "./multiRangeSlider.css";

const MultiRangeSlider = ({
  min,
  max,
  minValue,
  maxValue,
  onChange,
  is_mortgage_amount,
}) => {
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);
  const [isUserChanged, setIsUserChanged] = useState(false); // ✅ track manual changes

  const minValRef = useRef(null);
  const maxValRef = useRef(null);
  const range = useRef(null);
  const didMountRef = useRef(false); // ✅ to avoid first render trigger

  useEffect(() => {
    if (minValue !== undefined) setMinVal(minValue);
  }, [minValue]);

  useEffect(() => {
    if (maxValue !== undefined) setMaxVal(maxValue);
  }, [maxValue]);

  const getPercent = useCallback(
    (value) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, maxVal, getPercent]);

  useEffect(() => {
    if (didMountRef.current && isUserChanged) {
      onChange({ min: minVal, max: maxVal });
      setIsUserChanged(false); // reset after change
    } else {
      didMountRef.current = true;
    }
  }, [minVal, maxVal, isUserChanged, onChange]);

  const handleMinChange = (e) => {
    const value = Math.min(+e.target.value, maxVal - 1);
    setMinVal(value);
    setIsUserChanged(true); // ✅ mark as user change
    e.target.value = value.toString();
  };

  const handleMaxChange = (e) => {
    const value = Math.max(+e.target.value, minVal + 1);
    setMaxVal(value);
    setIsUserChanged(true); // ✅ mark as user change
    e.target.value = value.toString();
  };

  return (
    <div className="container">
      <input
        type="range"
        min={min}
        max={max}
        value={minVal}
        ref={minValRef}
        onChange={handleMinChange}
        className={classnames("thumb thumb--zindex-3", {
          "thumb--zindex-5": minVal > max - 100,
        })}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={maxVal}
        ref={maxValRef}
        onChange={handleMaxChange}
        className="thumb thumb--zindex-4"
      />
      <div className="slider">
        <div className="slider__track" />
        <div ref={range} className="slider__range" />
        <div className="slider__left-value">USD {minVal}+</div>
        <div className="slider__right-value">USD {maxVal}+</div>
      </div>
    </div>
  );
};

MultiRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};
export default MultiRangeSlider;
